import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Network, 
  FileSearch, 
  BarChart3, 
  Users,
  ChevronRight,
  ExternalLink,
  BookOpen,
} from 'lucide-react';

// Research data - Step 10
const researchData = {
  'unified-transaction-graph': {
    title: 'Unified Transaction Graph',
    subtitle: 'A single source of truth for all enterprise decisions',
    icon: Network,
    color: '#1A3A8F',
    summary: `The Unified Transaction Graph (UTG) is Liquicent's core data structure that connects all agents, 
    workflows, and decisions into a single, immutable record. Every action taken by an agent and every 
    decision made through a workflow is recorded as a transaction in the graph.`,
    keyPoints: [
      'Immutable record of all organizational decisions',
      'Causal relationships between decisions are preserved',
      'Enables replay and audit of any workflow execution',
      'Supports complex queries across organizational processes',
      'Foundation for compliance and governance reporting',
    ],
    applications: [
      'Regulatory compliance (SOC 2, HIPAA, SOX)',
      'Root cause analysis for process failures',
      'Decision impact analysis across workflows',
      'Knowledge transfer and organizational learning',
    ],
    paperLink: '#',
  },
  'decisiontrace': {
    title: 'DecisionTrace',
    subtitle: 'Explainable AI decisions for enterprise workflows',
    icon: FileSearch,
    color: '#2CB1A1',
    summary: `DecisionTrace is Liquicent's framework for making AI agent decisions explainable and auditable. 
    Every decision made by an agent includes a complete trace of the reasoning process, evidence considered, 
    and confidence levels.`,
    keyPoints: [
      'Full reasoning chain for every agent decision',
      'Evidence linking to source documents and data',
      'Confidence scoring with calibrated uncertainty',
      'Human-readable explanations for stakeholders',
      'Integration with the Unified Transaction Graph',
    ],
    applications: [
      'Audit preparation and compliance reviews',
      'Agent debugging and improvement',
      'Stakeholder communication and buy-in',
      'Training data generation for model improvement',
    ],
    paperLink: '#',
  },
  'workrank': {
    title: 'WorkRank',
    subtitle: 'Prioritizing workflows based on organizational impact',
    icon: BarChart3,
    color: '#1A3A8F',
    summary: `WorkRank is an algorithm that analyzes the Unified Transaction Graph to identify which workflows 
    have the highest impact on organizational outcomes. It helps enterprises prioritize automation efforts 
    and resource allocation.`,
    keyPoints: [
      'Graph-based analysis of workflow dependencies',
      'Impact scoring based on downstream effects',
      'Bottleneck identification in organizational processes',
      'Resource optimization recommendations',
      'Continuous learning from workflow outcomes',
    ],
    applications: [
      'Automation prioritization and ROI analysis',
      'Resource allocation for workflow optimization',
      'Process improvement identification',
      'Strategic planning for digital transformation',
    ],
    paperLink: '#',
  },
  'sociotrank': {
    title: 'SocioTRank',
    subtitle: 'Trust scoring for AI agents in enterprise environments',
    icon: Users,
    color: '#2CB1A1',
    summary: `SocioTRank is Liquicent's trust scoring system for AI agents. It combines performance metrics, 
    security assessments, and community feedback to provide a comprehensive trust score that enterprises 
    can use when selecting agents.`,
    keyPoints: [
      'Multi-factor trust scoring model',
      'Performance history across deployments',
      'Security and compliance verification',
      'Community feedback integration',
      'Continuous monitoring and score updates',
    ],
    applications: [
      'Agent selection for enterprise workflows',
      'Risk assessment for third-party agents',
      'Compliance verification for agent deployments',
      'Marketplace curation and quality control',
    ],
    paperLink: '#',
  },
};

// Research Hub (index page)
export const ResearchHub = () => {
  return (
    <div className="min-h-screen bg-[#F6F7FB]">
      <Helmet>
        <title>Research | Liquicent</title>
        <meta name="description" content="The science behind Liquicent's coordination infrastructure." />
      </Helmet>

      {/* Hero */}
      <section className="pt-32 pb-16 bg-white border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl lg:text-5xl font-bold text-[#0F172A] mb-6">
              Research
            </h1>
            <p className="text-xl text-[#475569] leading-relaxed">
              The science behind coordination infrastructure for autonomous enterprises
            </p>
          </div>
        </div>
      </section>

      {/* Research Papers */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            {Object.entries(researchData).map(([id, research]) => {
              const ResearchIcon = research.icon;
              return (
                <Link key={id} to={`/research/${id}`}>
                  <Card className="p-8 bg-white border border-[#E5E7EB] rounded-2xl hover:shadow-lg hover:border-[#1A3A8F]/20 transition-all h-full group">
                    <div 
                      className="w-14 h-14 rounded-xl flex items-center justify-center mb-6"
                      style={{ backgroundColor: `${research.color}10` }}
                    >
                      <ResearchIcon className="w-7 h-7" style={{ color: research.color }} />
                    </div>
                    <h3 className="text-xl font-bold text-[#0F172A] mb-2 group-hover:text-[#1A3A8F] transition-colors">
                      {research.title}
                    </h3>
                    <p className="text-[#475569] mb-4">{research.subtitle}</p>
                    <span className="text-sm text-[#1A3A8F] font-medium inline-flex items-center">
                      Read more
                      <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Publications */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-[#0F172A] mb-8">Publications</h2>
          <div className="space-y-4">
            {[
              { title: 'Coordination Infrastructure for Autonomous Enterprises', venue: 'ICSE 2025', type: 'Conference Paper' },
              { title: 'The Unified Transaction Graph: A Data Structure for Enterprise Decisions', venue: 'VLDB 2024', type: 'Research Paper' },
              { title: 'SocioTRank: Trust Scoring for AI Agents', venue: 'NeurIPS 2024', type: 'Workshop Paper' },
            ].map((pub, i) => (
              <Card key={i} className="p-6 bg-[#F6F7FB] border border-[#E5E7EB] rounded-xl flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-[#0F172A] mb-1">{pub.title}</h3>
                  <p className="text-sm text-[#64748B]">{pub.venue} • {pub.type}</p>
                </div>
                <Button variant="ghost" size="sm" className="text-[#1A3A8F]">
                  <ExternalLink className="w-4 h-4" />
                </Button>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

// Individual Research Page
const ResearchPage = () => {
  const { researchId } = useParams();
  const research = researchData[researchId];

  if (!research) {
    return (
      <div className="min-h-screen bg-[#F6F7FB] pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h1 className="text-2xl font-bold text-[#0F172A] mb-4">Research not found</h1>
          <Link to="/research" className="text-[#1A3A8F] hover:underline">Return to Research</Link>
        </div>
      </div>
    );
  }

  const ResearchIcon = research.icon;

  return (
    <div className="min-h-screen bg-[#F6F7FB]">
      <Helmet>
        <title>{research.title} | Liquicent Research</title>
        <meta name="description" content={research.subtitle} />
      </Helmet>

      {/* Hero */}
      <section className="pt-32 pb-12 bg-white border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Link to="/research" className="text-sm text-[#64748B] hover:text-[#1A3A8F] mb-6 inline-flex items-center">
            <ChevronRight className="w-4 h-4 mr-1 rotate-180" />
            Back to Research
          </Link>
          <div className="flex items-start gap-6 mt-4">
            <div 
              className="w-16 h-16 rounded-2xl flex items-center justify-center"
              style={{ backgroundColor: `${research.color}10` }}
            >
              <ResearchIcon className="w-8 h-8" style={{ color: research.color }} />
            </div>
            <div>
              <h1 className="text-3xl lg:text-4xl font-bold text-[#0F172A] mb-2">
                {research.title}
              </h1>
              <p className="text-lg text-[#475569]">{research.subtitle}</p>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-3 gap-12">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-12">
            {/* Summary */}
            <section>
              <h2 className="text-xl font-bold text-[#0F172A] mb-4">Summary</h2>
              <p className="text-[#475569] leading-relaxed whitespace-pre-line">{research.summary}</p>
            </section>

            {/* Key Points */}
            <section>
              <h2 className="text-xl font-bold text-[#0F172A] mb-4">Key Points</h2>
              <div className="space-y-3">
                {research.keyPoints.map((point, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div 
                      className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                      style={{ backgroundColor: `${research.color}10` }}
                    >
                      <span className="text-xs font-semibold" style={{ color: research.color }}>{i + 1}</span>
                    </div>
                    <span className="text-[#475569]">{point}</span>
                  </div>
                ))}
              </div>
            </section>

            {/* Applications */}
            <section>
              <h2 className="text-xl font-bold text-[#0F172A] mb-4">Applications</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {research.applications.map((app, i) => (
                  <Card key={i} className="p-4 bg-white border border-[#E5E7EB] rounded-lg">
                    <span className="text-[#475569]">{app}</span>
                  </Card>
                ))}
              </div>
            </section>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <Card className="p-6 bg-white border border-[#E5E7EB] rounded-xl">
              <h3 className="font-semibold text-[#0F172A] mb-4">Resources</h3>
              <div className="space-y-3">
                <a 
                  href={research.paperLink}
                  className="flex items-center gap-3 p-3 bg-[#F6F7FB] rounded-lg hover:bg-[#1A3A8F]/5 transition-colors"
                >
                  <BookOpen className="w-5 h-5 text-[#1A3A8F]" />
                  <span className="text-sm text-[#475569]">Read Paper</span>
                </a>
                <a 
                  href="#"
                  className="flex items-center gap-3 p-3 bg-[#F6F7FB] rounded-lg hover:bg-[#1A3A8F]/5 transition-colors"
                >
                  <ExternalLink className="w-5 h-5 text-[#1A3A8F]" />
                  <span className="text-sm text-[#475569]">View on arXiv</span>
                </a>
              </div>
            </Card>

            <Card className="p-6 bg-gradient-to-br from-[#1A3A8F] to-[#142D70] text-white rounded-xl">
              <h3 className="font-semibold mb-2">See It in Action</h3>
              <p className="text-sm text-white/80 mb-4">
                Learn how {research.title} is used in production workflows.
              </p>
              <Link to="/request-demo">
                <Button className="w-full bg-white text-[#1A3A8F] hover:bg-white/90">
                  Request Demo
                </Button>
              </Link>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResearchPage;
